<template>
    <!-- Filters -->
    <div class="mb-4 flex gap-4 flex-wrap">
        <div class="flex-1 min-w-[250px]">
            <IconField iconPosition="left">
                <InputIcon class="fa-solid fa-search"></InputIcon>
                <InputText :model-value="search" :placeholder="t('users.searchByName')" class="w-full"
                    @input="$emit('search', ($event.target as HTMLInputElement).value)" />
            </IconField>
        </div>
        <div class="min-w-[200px]">
            <Select :model-value="statusFilter" :options="statusOptions" optionLabel="label" optionValue="value"
                :placeholder="t('status.filterByStatus')" class="w-full"
                @change="$emit('statusChange', $event.value)" />
        </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card v-for="n in 6" :key="n">
            <template #content>
                <Skeleton height="180px" />
            </template>
        </Card>
    </div>

    <!-- Empty State -->
    <div v-else-if="users.length === 0" class="text-center py-12 text-gray-500">
        <i class="fa-solid fa-users text-3xl mb-3"></i>
        <p class="font-semibold">{{ t('users.noUsersFound') }}</p>
    </div>

    <!-- Cards Grid -->
    <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card v-for="user in users" :key="user.id" class="hover:shadow-lg transition-shadow">
                <template #header>
                    <div class="p-3 bg-primary-50 border-b">
                        <div class="flex items-center gap-3">
                            <Avatar :label="getUserInitials(user)" size="large" shape="circle"
                                class="bg-primary-500 text-white" />
                            <div class="flex-1">
                                <h3 class="text-xl font-bold text-primary-700">
                                    {{ user.firstName }} {{ user.lastName }}
                                </h3>
                                <p class="text-gray-600">{{ user.username }}</p>
                            </div>
                        </div>
                    </div>
                </template>
                <template #content>
                    <div class="space-y-2">
                        <div class="flex items-center gap-2">
                            <i class="fa-solid fa-envelope text-gray-500 w-5"></i>
                            <span>{{ user.email }}</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <i class="fa-solid fa-user-tag text-gray-500 w-5"></i>
                            <span>{{ user.role.name }}</span>
                        </div>
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-between items-center pt-1">
                        <Tag :value="user.enabled ? t('common.enabled') : t('common.disabled')"
                            :severity="user.enabled ? 'success' : 'danger'" class="px-3 py-1" />
                        <div class="flex gap-1">
                            <Button v-if="user.editable" icon="fa-solid fa-pen-to-square" text rounded severity="info"
                                size="large" v-tooltip.top="t('users.edit')" @click="$emit('edit', user)" />
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Paginator -->
        <Paginator :first="first" :rows="rows" :totalRecords="totalRecords" @page="$emit('page', $event)" class="mt-4"
            style="border: 1px solid var(--surface-border)" />
    </div>
</template>

<script setup lang="ts">
import type { User } from '@/lib/types/user';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    users: User[];
    loading: boolean;
    search: string;
    statusFilter: boolean | null;
    first: number;
    rows: number;
    totalRecords: number;
}

defineProps<Props>();

defineEmits<{
    search: [value: string];
    statusChange: [value: boolean | null];
    page: [event: any];
    edit: [user: User];
}>();

const statusOptions = computed(() => [
    { label: t('status.all'), value: null },
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);

function getUserInitials(user: User): string {
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || user.username.charAt(0).toUpperCase();
}
</script>
